import React, { useEffect, useImperativeHandle, useRef } from 'react'
import { Text } from 'react-native'
import type { TextInputProps } from 'react-native'
import type { OtpInputRef as CodeInputBaseRef } from 'react-native-otp-entry'
import { OtpInput as CodeInputBase } from 'react-native-otp-entry'

import {
  disabledCodeTextStyle,
  errorMessage as errorMessageStyles,
  getContainerStyles,
  getFocusStickStyles,
  getInputContainerStyles,
  getInputStyles,
  selectionColor,
} from './styles'
import type { CodeInputAutoComplete, CodeInputProps } from './types'

const DEFAULT_NUMBER_OF_INPUTS = 4

const AUTO_COMPLETE_PROPS: Partial<Record<CodeInputAutoComplete, TextInputProps>> = {
  postalCode: { autoComplete: 'postal-code', textContentType: 'postalCode' },
  off: { autoComplete: 'off', textContentType: 'none' },
}

const getTextInputProps = (props: CodeInputProps): TextInputProps => ({
  testID: props.testID,
  accessibilityLabel: props.label,
  ...(props.autoCompleteType && AUTO_COMPLETE_PROPS[props.autoCompleteType]),
  ...props.textInputProps,
  keyboardType: props.textInputProps?.keyboardType ?? 'phone-pad',
})

const getDisabledCodePlaceholder = (
  disabled: boolean | undefined,
  code: string | undefined,
  numberOfDigits: number
) => (disabled && code ? code.padEnd(numberOfDigits, ' ') : undefined)

const shouldAutoFocus = (props: CodeInputProps) => !props.disabled && (props.autoFocus ?? true)

const useCodeSync = (props: CodeInputProps) => {
  const internalRef = useRef<CodeInputBaseRef>(null)
  const baseRef = props.codeRef ?? internalRef
  const lastCode = useRef<string | undefined>(undefined)
  const inputCode = useRef('')
  const applyingCode = useRef<string | undefined>(undefined)
  const code = props.value ?? lastCode.current

  useEffect(() => {
    if (props.disabled) {
      inputCode.current = ''
      return
    }
    if (code === undefined || code === inputCode.current) {
      return
    }
    applyingCode.current = code
    baseRef.current?.setValue(code)
    applyingCode.current = undefined
  }, [code, baseRef, props.disabled])

  useImperativeHandle(props.fieldRef, () => ({ focus: () => baseRef.current?.focus() }), [baseRef])

  const onTextChange = (next: string) => {
    inputCode.current = next
    lastCode.current = next
    if (next === applyingCode.current) {
      return
    }
    props.handleChange(next)
  }

  return { baseRef, code, onTextChange }
}

const CodeInput = (props: CodeInputProps) => {
  const numberOfDigits = props.numberOfDigits || DEFAULT_NUMBER_OF_INPUTS
  const { baseRef, code, onTextChange } = useCodeSync(props)

  return (
    <>
      <CodeInputBase
        key={props.disabled ? 'disabled' : 'enabled'}
        placeholder={getDisabledCodePlaceholder(props.disabled, code, numberOfDigits)}
        ref={baseRef}
        numberOfDigits={numberOfDigits}
        autoFocus={shouldAutoFocus(props)}
        disabled={props.disabled}
        type="numeric"
        secureTextEntry={props.isSecure}
        theme={{
          pinCodeTextStyle: getInputStyles(props.codeStatus, Boolean(props.isSecure)),
          pinCodeContainerStyle: getInputContainerStyles(props.codeStatus),
          containerStyle: getContainerStyles(props.align),
          focusStickStyle: getFocusStickStyles(props.codeStatus),
          placeholderTextStyle: disabledCodeTextStyle,
        }}
        focusColor={selectionColor}
        onTextChange={onTextChange}
        onBlur={props.onBlur}
        textInputProps={getTextInputProps(props)}
      />
      {props.errorMessage ? (
        <Text className={errorMessageStyles()}>{props.errorMessage}</Text>
      ) : null}
    </>
  )
}

export default CodeInput
